import { subscribeToGlobalEvent } from "@storytell/events";
import { StButton } from "@storytell/ui/buttons";
import { LayoutProvider } from "@storytell/ui/layout";
import { TbArrowsDiagonal } from "solid-icons/tb";
import type { Accessor, Component } from "solid-js";
import { createSignal, onCleanup, onMount, Show, Suspense } from "solid-js";
import { LuPanelRightFilled } from "~/components/icons/LuPanelRightFilled";
import { LogoLoading } from "~/components/loading/LogoLoading";
import { clearCommandCenterDocked } from "~/components/prompt/internal/activePromptEditor";
import { useI18n } from "~/i18n/context";
import { useWire } from "~/wire";
import { PageRenderer } from "./PageRenderer";

interface SidePanelProps {
  /** Whether the side panel is effectively hidden */
  isHidden: Accessor<boolean>;
  /** Whether the side panel is visually collapsed (for border animation) */
  isVisuallyCollapsed: Accessor<boolean>;
  /** CSS transition style for width animations */
  transitionStyle: Accessor<string | undefined>;
}

/** Side panel component that displays secondary content alongside the main view */
export const SidePanel: Component<SidePanelProps> = (props) => {
  const wire = useWire();
  const layout = wire.ui.layout;
  const nav = wire.navigation;

  // Highlight animation state
  const [isHighlighted, setIsHighlighted] = createSignal(false);

  onMount(() => {
    const unsubscribe = subscribeToGlobalEvent("highlight-side-panel", () => {
      setIsHighlighted(true);
      setTimeout(() => setIsHighlighted(false), 600);
    });
    onCleanup(unsubscribe);
  });

  return (
    <div
      class="overflow-hidden bg-panel pt-10"
      classList={{
        // Large screens: inline flex item
        "h-[calc(100%-0.25rem)] shrink-0 grow-0 relative m-0.5 rounded-2xl": layout.isLargeScreen(),
        // Medium/small screens: fixed overlay
        "fixed right-0 top-8 bottom-0 z-40 shadow-xl rounded-l-2xl": !layout.isLargeScreen(),
        // Border when visible
        "border border-primary-border": !props.isVisuallyCollapsed(),
        // When hidden on large screens, don't capture pointer so the floating timeline can receive clicks
        "pointer-events-none": layout.isLargeScreen() && props.isHidden(),
      }}
      style={{
        width: layout.isLargeScreen()
          ? `${layout.sideSize()}px`
          : props.isHidden()
            ? "0px"
            : layout.isSmallScreen()
              ? "calc(100vw - 3.5rem)"
              : `${layout.sideExpandedSize()}px`,
        transition: [props.transitionStyle(), "box-shadow 300ms ease-out"].filter(Boolean).join(", "),
        "box-shadow": isHighlighted() ? "inset 0 0 0 2px var(--color-brand)" : "inset 0 0 0 2px transparent",
      }}
    >
      <SidePanelContent layout={layout} nav={nav} />
    </div>
  );
};

/** Inner content of the side panel: the action row and the page body. */
const SidePanelContent: Component<{
  nav: ReturnType<typeof useWire>["navigation"];
  layout: ReturnType<typeof useWire>["ui"]["layout"];
}> = (props) => {
  const wire = useWire();
  const { t } = useI18n();
  const hasAnyAssets = () => (wire.assets.getTotalCount() ?? 0) > 0;
  const isCommandCenter = () => props.nav.state().sideId === "command-center";
  const isScopePicker = () => props.nav.state().sideId === "scope-picker";
  return (
    <>
      {/* Action buttons row: Swap panels; pl-10 reserves room for the close-panel toggle overlay. */}
      <div class="absolute inset-x-2 top-2 z-20 flex items-center justify-between gap-2 pl-10">
        <div class="relative top-0.5 flex items-center gap-1">
          <Show when={!props.layout.isSmallScreen() && isCommandCenter()}>
            <StButton
              icon={LuPanelRightFilled}
              noBorder
              title="Unpin from side panel"
              class="bg-panel/80 text-brand backdrop-blur-sm hover:bg-panel-hover dark:border-white/10"
              onClick={() => {
                clearCommandCenterDocked();
                props.nav.closeSidePanel();
              }}
            />
          </Show>
          <Show when={!isCommandCenter() && !isScopePicker() && hasAnyAssets() && !props.layout.isSmallScreen()}>
            <StButton
              icon={TbArrowsDiagonal}
              noBorder
              title={t("action.open_in_main_view")}
              class="bg-panel/80 backdrop-blur-sm hover:bg-panel-hover dark:border-white/10"
              onClick={() => props.nav.promoteSidePanel()}
            />
          </Show>
        </div>
      </div>

      {/* Inner content maintains full width and slides in from right */}
      <div
        class="h-full"
        style={{
          width: props.layout.isSmallScreen() ? "100%" : `${props.layout.sideExpandedSize()}px`,
          "margin-left": "auto",
        }}
      >
        <Show when={props.nav.state().sideId}>
          <LayoutProvider side>
            {/* Page bodies are lazy, and this panel sits outside the main
                content's boundary — without one here a side-panel open
                escalates to the root splash and blanks the whole app. */}
            <Suspense fallback={<LogoLoading />}>
              <PageRenderer id={props.nav.state().sideId} />
            </Suspense>
          </LayoutProvider>
        </Show>
      </div>
    </>
  );
};
